import { useDeleteDocument } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { downloadFileByUrl } from "@shared/utils/utils";
import { Download, FileText, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAssetDetail, useUpdateAsset } from "../../hooks";

interface Step4DocumentationProps {
  editMode: boolean;
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  allStepData?: Record<number, any>;
  onComplete?: (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => void;
}

const Step4Documentation: React.FC<Step4DocumentationProps> = ({
  editMode,
  stepHelpers,
  currentStepIndex = 3,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
  allStepData,
  onComplete,
}) => {
  const { id, action } = useParams();
  const { data: assetDetail } = useAssetDetail(id);
  const navigate = useNavigate();
  const dataLoadedRef = useRef(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const { mutateAsync: removeDocument, isPending: isDeleting } =
    useDeleteDocument("asset");
  // API mutation hook
  const { mutateAsync: updateAsset, isPending: isUpdating } =
    useUpdateAsset(id);

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback((stepData: any) => {
    if (!stepHelpers) return;

    try {
      const dataToSave = {
        ...stepData,
        timestamp: new Date().toISOString(),
        stepIndex: currentStepIndex,
      };

      stepHelpers.setStepData(currentStepIndex, dataToSave);
    } catch (error) {
      console.error("Failed to save step 4 data:", error);
    }
  }, []);

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};

    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      console.error("Failed to get step 4 data:", error);
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  // Helper function to get data from previous steps
  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return {};

    try {
      return stepHelpers.getStepData(0) || {}; // Step 1 is at index 0
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return {};
    }
  }, [stepHelpers]);

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        const step1Data = getStep1Data();

        if (savedStepData && Object.keys(savedStepData).length > 0) {
          // Restore uploaded files
          if (savedStepData.documents) {
            setUploadedFiles(savedStepData.documents);
          }

          // Call onDataChange to sync with stepper
          onDataChange?.(savedStepData);
        }

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step 4 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData, getStep1Data, onDataChange]);

  // Auto-save when files
  useEffect(() => {
    if (dataLoadedRef.current) {
      const updatedData = {
        ...data,
        documents: uploadedFiles,
        lastUpdated: new Date().toISOString(),
      };
      onDataChange?.(updatedData);
      saveStepData(updatedData);
    }
  }, [uploadedFiles]);

  const handleComplete = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    try {
      const formData = form.getValues();

      const apiFormData = new FormData();

      if (Array.isArray(formData.documents) && formData.documents.length > 0) {
        formData.documents.forEach((doc) => {
          apiFormData.append("documents", doc);
        });
      }

      const response = await updateAsset(apiFormData);

      // Get all step data for completion callback
      if (response) {
        navigate("/ax/asset-registry/assets");
      }
    } catch (error) {
      console.error("Failed to complete asset creation:", error);
      if (setValidationError) {
        setValidationError(
          "An error occurred while finalizing the asset. Please try again."
        );
      }
    }
  };

  const handlePrevious = () => {
    // Save current state before going back
    const currentData = {
      ...data,
      documents: uploadedFiles,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    };

    onDataChange?.(currentData);
    saveStepData(currentData);
    onPrevious?.();
  };

  const step1Data = getStep1Data();

  const form = FormService.createForm({
    fields: [],
    defaultValues: data,
  });

  const formFields: FormField[] = [
    {
      name: "documents",
      type: "multi-file",
      label: "Upload photos, videos, and documents related to this asset",
      className: "col-span-2",
      placeholder: "Click to upload files",
      required: false,
      multiple: true,
      accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
      classes: FIELD_CLASSES,
    },
  ];

  const removeExistingDocument = (documentId: string) => {
    removeDocument(documentId);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Documentation & Files
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          Upload supporting documentation, photos, and files to complete your
          asset record.
        </p>
      </div>

      <div className="space-y-8">
        <div>
          <DynamicForm
            fields={formFields}
            form={form}
            config={{
              layout: "vertical",
              showSubmitButton: false,
              showResetButton: false,
              spacing: "medium",
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {assetDetail?.uploadedDocuments?.map((uploadedFile) => (
          <div
            key={uploadedFile.id}
            className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
          >
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {/* File Icon */}
              <div className="flex-shrink-0">
                <FileText className="w-5 h-5 text-red-500"></FileText>
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {uploadedFile.fileName}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end">
              <Button
                onClick={() => downloadFileByUrl(uploadedFile.fileUrl)}
                className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                variant="ghost"
              >
                <Download className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => removeExistingDocument(uploadedFile.id)}
                title="Remove"
                variant="ghost"
                isLoading={isDeleting}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button
          onClick={handlePrevious}
          disabled={isUpdating}
          variant="outline"
        >
          Previous
        </Button>
        <Button onClick={handleComplete} isLoading={isUpdating}>
          {action === "edit" ? "Submit" : "Submit"}
        </Button>
      </div>
    </div>
  );
};

export default Step4Documentation;
